import { env } from '../config/env';
import type { ApiErrorBody } from '../types/api';
import { filenameFromContentDisposition, type DownloadedFile } from '../utils/downloadBlob';
import { clearAuthSession } from './authSession';
import { ApiAuthError, ApiError, ApiForbiddenError } from './errors';
import { tokenStorage } from './tokenStorage';

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  auth?: boolean;
}

function buildUrl(path: string): string {
  if (path.startsWith('http')) return path;
  return `${env.apiUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

async function handleErrorResponse(
  response: Response,
  auth: boolean,
  hadToken: boolean,
): Promise<never> {
  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const data = isJson ? await response.json() : await response.text();

  const errorBody: ApiErrorBody =
    typeof data === 'object' && data !== null && 'message' in data
      ? (data as ApiErrorBody)
      : { message: typeof data === 'string' && data ? data : response.statusText };

  if (response.status === 401 && auth && hadToken) {
    clearAuthSession();
    throw new ApiAuthError(errorBody);
  }

  if (response.status === 401 && auth) {
    throw new ApiAuthError(errorBody);
  }

  if (response.status === 403) {
    throw new ApiForbiddenError(errorBody);
  }

  throw new ApiError(response.status, errorBody);
}

function buildAuthHeaders(options: RequestOptions): { headers: Headers; hadToken: boolean } {
  const { auth = true, headers } = options;
  const requestHeaders = new Headers(headers);

  if (auth) {
    const token = tokenStorage.get();
    if (token) {
      requestHeaders.set('Authorization', `Bearer ${token}`);
    }
  }

  return { headers: requestHeaders, hadToken: requestHeaders.has('Authorization') };
}

export async function apiClient<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, auth = true, headers, ...rest } = options;

  const requestHeaders = new Headers(headers);

  if (body !== undefined && !(body instanceof FormData)) {
    requestHeaders.set('Content-Type', 'application/json');
  }

  if (auth) {
    const token = tokenStorage.get();
    if (token) {
      requestHeaders.set('Authorization', `Bearer ${token}`);
    }
  }

  const hadToken = requestHeaders.has('Authorization');

  const response = await fetch(buildUrl(path), {
    ...rest,
    headers: requestHeaders,
    body: body instanceof FormData ? body : body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const data = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    const errorBody: ApiErrorBody =
      typeof data === 'object' && data !== null && 'message' in data
        ? (data as ApiErrorBody)
        : { message: typeof data === 'string' && data ? data : response.statusText };

    if (response.status === 401 && auth && hadToken) {
      clearAuthSession();
      throw new ApiAuthError(errorBody);
    }

    if (response.status === 401 && auth) {
      throw new ApiAuthError(errorBody);
    }

    if (response.status === 403) {
      throw new ApiForbiddenError(errorBody);
    }

    throw new ApiError(response.status, errorBody);
  }

  return data as T;
}

export async function apiDownload(path: string, options: RequestOptions = {}): Promise<DownloadedFile> {
  const { auth = true, body: _body, ...rest } = options;
  const { headers, hadToken } = buildAuthHeaders({ auth, headers: options.headers });

  const response = await fetch(buildUrl(path), {
    ...rest,
    headers,
  });

  if (!response.ok) {
    return handleErrorResponse(response, auth, hadToken);
  }

  const blob = await response.blob();
  const filename =
    filenameFromContentDisposition(response.headers.get('content-disposition')) ??
    `fatura-${path.split('/').at(-2) ?? 'siparis'}`;

  return { blob, filename };
}
